// Pending Matches (PD-13891) filters — Search / State / Type(source), plus the
// PD-14449 compliance-officer set (Customer/Lead, confidence, new-vs-linked).
// These render the filter tray on the consolidated match-review list
// (listKey 'license_match'). The backend handler is the custom
// GET /api/licenses/matches/list endpoint, which reads these query params from
// the filters JSON (matchKeywords / matchState / matchSource /
// matchLeadOrCustomer / matchConfidence / matchNewLeadsOnly), so no shared
// queryMapping is needed here.
//
// PD-15865 (Chris 2026-09-03) — the reviewer's filter set, short Title Case
// labels: 'License Type' (matchSource), 'Category' (matchCategory), 'State'
// (matchState), 'Active Buyers' (matchActiveBuyer), 'Confidence'
// (matchConfidence, now bands) and 'Source' (matchMethod, the matchmaker pass).
// Every option id is non-numeric so it survives the list's URL state
// (ListStateStorageService.parseUrlParams turns parseInt-able ids into
// numbers).

import {
	LICENSE_CATEGORIES,
	LICENSE_CATEGORY_LABEL,
	LICENSE_SOURCE_TYPE_LABEL,
	MATCH_CONFIDENCE_BANDS,
	MATCHMAKER_SOURCE_TYPES,
	matchMethodLabel,
	PENDING_MATCH_METHODS,
} from '@supply-mover-2-0/shared';
import { createKeywordFilter } from './factories';
import type { FilterRegistry } from './types';

export const licenseMatchFilters: FilterRegistry = {
	matchKeywords: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search matches...',
			objectTable: 'client_license_match',
			listKeys: ['license_match'],
		}),
		queryParamName: 'matchKeywords',
	},

	matchState: {
		type: 'multi-select',
		label: 'State',
		placeholder: 'Select state...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		apiEndpoint: '/licenses/state/options',
		queryParamName: 'matchState',
		multiple: true,
		valueExtractor: {
			property: 'id',
		},
		format: 'array',
		listKeys: ['license_match'],
	},

	matchSource: {
		type: 'multi-select',
		label: 'License Type',
		placeholder: 'Select license type...',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'matchSource',
		multiple: true,
		valueExtractor: {
			property: 'id',
		},
		// Normalized registry source classifications (lm_registry.source_type),
		// denormalized onto client_license_match.registry_source_type. PD-15865:
		// every value the registry holds (the list lacked DEA CMEA, DEA Chemical
		// and State Drug Permit); OTHER is left out because no registry row
		// carries it. The ticket's "State Control" is Controlled Substance (plus
		// State Drug Permit); its "KYC" is no longer a license type (PD-14498).
		options: MATCHMAKER_SOURCE_TYPES.map((value) => ({
			id: value,
			label: LICENSE_SOURCE_TYPE_LABEL[value],
		})),
		format: 'array',
		listKeys: ['license_match'],
	},

	// PD-15865 — the registry's granular category (lm_registry.license_category:
	// pharmacy facility, pharmacist, veterinarian …). Not denormalized onto the
	// match row, so the endpoint tests it by the registry's primary key.
	matchCategory: {
		type: 'multi-select',
		label: 'Category',
		placeholder: 'Select category...',
		colorScheme: 'purple',
		searchable: true,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'matchCategory',
		multiple: true,
		valueExtractor: {
			property: 'id',
		},
		options: LICENSE_CATEGORIES.map((value) => ({
			id: value,
			label: LICENSE_CATEGORY_LABEL[value],
		})),
		format: 'array',
		listKeys: ['license_match'],
	},

	// PD-15865 — the Compliance dashboard's active-buyer population (the
	// endpoint reads the dashboard's own query), so the queue and the cards
	// count the same accounts. Same choices as the license list's
	// licenseActiveBuyer.
	matchActiveBuyer: {
		type: 'radio',
		label: 'Active Buyers',
		colorScheme: 'green',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'matchActiveBuyer',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'yes', label: 'Buying (sale in 90 days)' },
			{ id: 'no', label: 'Not buying' },
		],
		defaultValue: { id: 'all', label: 'All' },
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license_match'],
	},

	// PD-15865 — which matchmaker pass surfaced the suggestion
	// (client_license_match.match_method), in the words the review screen uses.
	matchMethod: {
		type: 'multi-select',
		label: 'Source',
		placeholder: 'Select source...',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'matchMethod',
		multiple: true,
		valueExtractor: {
			property: 'id',
		},
		options: PENDING_MATCH_METHODS.map((method) => ({
			id: method,
			label: matchMethodLabel(method),
		})),
		format: 'array',
		listKeys: ['license_match'],
	},

	// PD-14449: the three filters below were ALREADY wired end-to-end in the
	// backend (licenses.controller.ts listPendingMatches maps them; the service
	// applies the WHERE clauses) but never exposed as UI controls.

	// Customer vs Lead — mirrors client.ts leadOrCustomer (the customer ask).
	// The controller only applies exactly 'Lead' / 'Customer'; 'All' means no filter.
	matchLeadOrCustomer: {
		type: 'radio',
		label: 'Lead/Customer Status',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'value' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'matchLeadOrCustomer',
		multiple: false,
		options: [
			{ id: 'All', label: 'All' },
			{ id: 'Lead', label: 'Leads' },
			{ id: 'Customer', label: 'Customers' },
		],
		defaultValue: { id: 'All', label: 'All' },
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license_match'],
	},

	// Confidence band (PD-15865; was a "Min Confidence" radio, PD-14449). A
	// saved search still holding an old minimum ('90') is read as one by the
	// controller. The bands are MATCH_CONFIDENCE_BANDS, shared with the endpoint.
	matchConfidence: {
		type: 'multi-select',
		label: 'Confidence',
		placeholder: 'Select confidence...',
		colorScheme: 'green',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'matchConfidence',
		multiple: true,
		options: MATCH_CONFIDENCE_BANDS.map((band) => ({
			id: band.id,
			label: band.label,
		})),
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license_match'],
	},

	// New-license matches only (no existing client_license to link to) —
	// separates "brand-new license" review from "re-link to existing" review.
	// Controller accepts 'yes' (or boolean true); 'All' -> no filter.
	matchNewLeadsOnly: {
		type: 'radio',
		label: 'License Linkage',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'value' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'matchNewLeadsOnly',
		multiple: false,
		options: [
			{ id: 'All', label: 'All' },
			{ id: 'yes', label: 'New licenses only' },
		],
		defaultValue: { id: 'All', label: 'All' },
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license_match'],
	},

	// PD-16256 — found licenses that fill a NEED on an active buyer: no verified
	// state license, or buys controlled product with no verified DEA/CSR.
	matchFillsGap: {
		type: 'radio',
		label: 'Fills a Gap',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'value' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'matchFillsGap',
		multiple: false,
		options: [
			{ id: 'All', label: 'All' },
			{ id: 'yes', label: 'Found license fills a gap' },
		],
		defaultValue: { id: 'All', label: 'All' },
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license_match'],
	},

	// PD-16246 — suggestions to a registration the regulator shows as dead
	// (cancelled, revoked, retired, surrendered, suspended, inactive, deceased,
	// denied) cannot be confirmed, so the queue hides them by default.
	matchHideDead: {
		type: 'radio',
		label: 'Dead Registrations',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'value' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'matchHideDead',
		multiple: false,
		options: [
			{ id: 'yes', label: 'Hide dead registrations' },
			{ id: 'no', label: 'Show dead registrations' },
		],
		defaultValue: { id: 'yes', label: 'Hide dead registrations' },
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license_match'],
	},

	// PD-16246 — expired is its own switch: an expired license is often renewed,
	// and the suggestion reappears by itself when the regulator shows it current.
	matchHideExpired: {
		type: 'radio',
		label: 'Expired Registrations',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'value' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'matchHideExpired',
		multiple: false,
		options: [
			{ id: 'yes', label: 'Hide expired registrations' },
			{ id: 'no', label: 'Show expired registrations' },
		],
		defaultValue: { id: 'yes', label: 'Hide expired registrations' },
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license_match'],
	},
};
